import React, { useEffect, useState, useCallback } from "react";
import "../styles/AdminDashboard.css";
import {
  FaUser, FaClipboardList, FaGraduationCap, FaSignOutAlt, FaCogs,
  FaPlus, FaUserEdit, FaHistory, FaSave, FaTrash, FaNewspaper,
  FaBriefcase, FaEye, FaComments, FaDownload, FaUsers
} from "react-icons/fa";
import BlogManager from "./BlogManager"; 
import ServiceManager from "./ServiceManager";

// const API = "http://localhost:5000";
const API = "https://backend.afrivisionconsultants.com";
// const API = "https://backend.afrivisionconsultants.com";

export default function AdminDashboard() {
  // --- Core Data States ---
  const [applications, setApplications] = useState([]);
  const [roles, setRoles] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  
  // --- STATE FOR EDITING ROLES ---
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  // --- USER MANAGEMENT HANDLERS ---
  const startEditUser = (user) => {
    setUserForm({ username: user.username, email: user.email, role: user.role, password: "" });
    setEditingUserId(user.id);
    setShowPasswordReset(false); 
  };

  const handleUpdateUser = async () => {
    if (!editingUserId) return;
    try {
      const res = await fetch(`${API}/admin/users/${editingUserId}`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ 
          username: userForm.username, 
          email: userForm.email, 
          role: userForm.role 
        })
      });
      if (res.ok) {
        setEditingUserId(null);
        setUserForm({ username: "", password: "", email: "", role: "Editor" });
        fetchData();
        alert("User updated successfully!");
      }
    } catch (e) { console.error(e); }
  };

  const handlePasswordReset = async (userId) => {
    const pass = prompt("Enter the new password for this user:");
    if (!pass) return;
    
    try {
      const res = await fetch(`${API}/admin/users/${userId}/password`, {
        method: "PUT",
        headers: getHeaders(),
        body: JSON.stringify({ newPassword: pass })
      });
      if (res.ok) alert("Password changed successfully.");
      else alert("Failed to change password.");
    } catch (e) { console.error(e); }
  };

  // --- Stats State ---
  const [stats, setStats] = useState({ 
    applications: 0, roles: 0, services: 0, posts: 0, views: 0, comments: 0 
  });

  // --- UI States ---
  const [section, setSection] = useState("home"); 
  const [filterRole, setFilterRole] = useState("");
  
  // --- Forms ---
  const [roleForm, setRoleForm] = useState({ 
    title: "", 
    location: "", 
    type: "Full-Time", 
    description: "", 
    descriptionFile: null 
  });
  const [qualForm, setQualForm] = useState({ title: "" });
  const [userForm, setUserForm] = useState({ username: "", password: "", email: "", role: "Editor" });
  const [profileForm, setProfileForm] = useState({ name: "", password: "" });

  const token = localStorage.getItem("adminToken"); 
  const userRole = localStorage.getItem("role");

  // --- Fetch Helpers ---
  const getHeaders = useCallback(() => ({ 
    "Authorization": `Bearer ${token}`,
    "Content-Type": "application/json"
  }), [token]);

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/admin/dashboard-stats`, { headers: getHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error("Stats fetch error", e); }
  }, [getHeaders, token]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [appsRes, rolesRes, qualsRes, usersRes] = await Promise.all([
        fetch(`${API}/admin/applications`, { headers: getHeaders() }),
        fetch(`${API}/admin/roles`, { headers: getHeaders() }),
        fetch(`${API}/admin/qualifications`, { headers: getHeaders() }),
        fetch(`${API}/admin/users`, { headers: getHeaders() })
      ]);

      if (appsRes.ok) setApplications(await appsRes.json());
      if (rolesRes.ok) setRoles(await rolesRes.json());
      if (qualsRes.ok) setQualifications(await qualsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
    } catch (e) { console.error("Data fetch error", e); }
  }, [getHeaders, token]);

  const fetchAudit = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/admin/audit`, { headers: getHeaders() });
      if (res.ok) setAuditLogs(await res.json());
    } catch (e) { console.error("Audit error", e); }
  }, [getHeaders, token]);

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [fetchStats, fetchData]);

  useEffect(() => {
    if (section === "audit") fetchAudit();
  }, [section, fetchAudit]);

  // --- Action Handlers ---
  const handleLogout = () => {
    localStorage.clear();
    window.location.href = "/login"; 
  };

  const handleDelete = async (endpoint, id, refreshFn) => {
    if(!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await fetch(`${API}/admin/${endpoint}/${id}`, { method: "DELETE", headers: getHeaders() });
      refreshFn(); 
      fetchStats(); 
    } catch(e) { console.error(e); }
  };

  const handleCreate = async (endpoint, data, setForm, initialForm, refreshFn) => {
    try {
      const res = await fetch(`${API}/admin/${endpoint}`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify(data)
      });
      if(res.ok) {
        setForm(initialForm);
        refreshFn();
        fetchStats();
      } else {
        alert("Failed to create item. Check console for details.");
      }
    } catch(e) { console.error(e); }
  };

  // --- ROLE UPDATE HANDLERS ---
  const startEditRole = (role) => {
    setRoleForm({ 
      title: role.title, 
      location: role.location, 
      type: role.type, 
      description: role.description 
    });
    setEditingRoleId(role.id);
  };

  const handleCreateRole = async () => {
    const formData = new FormData();
    formData.append("title", roleForm.title);
    formData.append("location", roleForm.location);
    formData.append("type", roleForm.type);
    formData.append("description", roleForm.description);
    
    if (roleForm.descriptionFile) {
      formData.append("descriptionFile", roleForm.descriptionFile);
    }

    try {
      const res = await fetch(`${API}/admin/roles`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }, 
        body: formData
      });
      
      if (res.ok) {
        setRoleForm({ title: "", location: "", type: "Full-Time", description: "", descriptionFile: null });
        fetchData();     
        fetchStats();    
        alert("Role created successfully!");
      } else {
        alert("Failed to create role. Please try again.");
      }
    } catch (e) { console.error(e); }
  };

  const handleUpdateRole = async () => {
    if (!editingRoleId) return;
    
    const formData = new FormData();
    formData.append("title", roleForm.title);
    formData.append("location", roleForm.location);
    formData.append("type", roleForm.type);
    formData.append("description", roleForm.description);
    
    if (roleForm.descriptionFile) {
      formData.append("descriptionFile", roleForm.descriptionFile);
    }

    try {
      const res = await fetch(`${API}/admin/roles/${editingRoleId}`, {
        method: "PUT", 
        headers: { "Authorization": `Bearer ${token}` }, 
        body: formData
      });
      
      if (res.ok) {
        setRoleForm({ title: "", location: "", type: "Full-Time", description: "", descriptionFile: null });
        setEditingRoleId(null);
        fetchData();
      }
    } catch (e) { console.error(e); }
  };

  const fetchApplications = useCallback(async () => {
    try {
        const res = await fetch(`${API}/admin/applications`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if(res.ok) setApplications(await res.json());
    } catch(e) { console.error(e); }
  }, [token]);

  const handleDownload = async (filename) => {
    try {
        const res = await fetch(`${API}/admin/download/${filename}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } else {
            alert("File not found or error downloading.");
        }
    } catch (error) { console.error("Download error:", error); }
  };

  // --- Render ---
  return (
    <div className="admin-dashboard">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h3>Admin Panel</h3>
          <p>Role: {userRole}</p>
        </div>
        <nav>
          <button className={section === "home" ? "active" : ""} onClick={() => setSection("home")}><FaUsers /> Dashboard</button>
          <div className="nav-label">HR & Users</div>
          <button className={section === "applications" ? "active" : ""} onClick={() => setSection("applications")}><FaClipboardList /> Applications</button>
          <button className={section === "roles" ? "active" : ""} onClick={() => setSection("roles")}><FaBriefcase /> Roles & Jobs</button>
          <button className={section === "qualifications" ? "active" : ""} onClick={() => setSection("qualifications")}><FaGraduationCap /> Qualifications</button>
          <button className={section === "users" ? "active" : ""} onClick={() => setSection("users")}><FaUser /> Users</button>
          <div className="nav-label">Content</div>
          <button className={section === "services" ? "active" : ""} onClick={() => setSection("services")}><FaCogs /> Services</button>
          <button className={section === "blog" ? "active" : ""} onClick={() => setSection("blog")}><FaNewspaper /> Blog / News</button>
          <div className="nav-label">System</div>
          <button className={section === "audit" ? "active" : ""} onClick={() => setSection("audit")}><FaHistory /> Audit Logs</button>
          <button className={section === "profile" ? "active" : ""} onClick={() => setSection("profile")}><FaUserEdit /> Profile</button>
          <button className="logout" onClick={handleLogout}><FaSignOutAlt /> Logout</button>
        </nav>
      </aside>

      <main className="main-content">
        {/* 1. HOME DASHBOARD */}
        {section === "home" && (
          <div className="dashboard-home">
            <h2>Dashboard Overview</h2>
            <div className="stats-grid">
              <div className="stat-card"><div><h3>{stats.applications}</h3><p>Applications</p></div></div>
              <div className="stat-card"><div><h3>{stats.roles}</h3><p>Open Roles</p></div></div>
              <div className="stat-card"><div><h3>{stats.posts}</h3><p>Blog Posts</p></div></div>
              <div className="stat-card"><div><h3>{stats.views}</h3><p>Total Views</p></div></div>
            </div>
          </div>
        )}

        {/* 2. APPLICATIONS */}
        {section === "applications" && (
            <div className="module-container">
                <h2>Applications & Proposals</h2>
                <div className="actions-header">
                    <button className="refresh-btn" onClick={fetchApplications}>Refresh List</button>
                </div>
                
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Name/Org</th>
                            <th>Email</th>
                            <th>Role / Details</th>
                            <th>Experience</th>
                            <th>Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {applications.length === 0 ? (
                            <tr><td colSpan="7" className="text-center">No applications received yet.</td></tr>
                        ) : (
                            applications.map(app => (
                                <tr key={app.id}>
                                    <td>
                                        <span className={`app-badge ${app.type === 'PARTNERSHIP' ? 'badge-partnership' : 'badge-regular'}`}>
                                            {app.type}
                                        </span>
                                    </td>
                                    <td>{app.fullName}</td>
                                    <td>{app.email}</td>
                                    <td>{app.roleAppliedFor}</td>
                                    <td>{app.experience ? `${app.experience} Yrs` : '-'}</td>
                                    <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                                    <td>
                                        <div className="table-actions">
                                            {app.cvPath ? (
                                                <button 
                                                    className="action-btn download cursor-pointer" 
                                                    onClick={() => handleDownload(app.cvPath)}
                                                    title="Download File"
                                                >
                                                    <FaDownload />
                                                </button>
                                            ) : (
                                                <span className="no-file-text">No File</span>
                                            )}

                                            <button 
                                                className="action-btn delete-btn"
                                                onClick={() => handleDelete('application', app.id, fetchApplications)} 
                                                title="Delete Application"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        )}

        {/* 3. ROLES */}
        {section === "roles" && (
          <div className="manage-section">
            <h2>{editingRoleId ? "Edit Role" : "Manage Roles"}</h2>
            <div className="add-input form-column">
              <div className="form-row">
                <input 
                  placeholder="Job Title" 
                  value={roleForm.title} 
                  onChange={(e) => setRoleForm({...roleForm, title: e.target.value})} 
                />
                <input 
                  placeholder="Location" 
                  value={roleForm.location} 
                  onChange={(e) => setRoleForm({...roleForm, location: e.target.value})} 
                />
              </div>

              <div className="file-upload-box">
                <label className="bold-label">
                  Job Description (Choose One or Both):
                </label>
                
                <textarea 
                  className="full-width-textarea"
                  placeholder="Type the job description here..." 
                  value={roleForm.description} 
                  onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                />

                <div className="or-divider">OR</div>
                
                <label className="small-label">
                  Upload Description Document (PDF, Word):
                </label>
                <input 
                  type="file" 
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => setRoleForm({...roleForm, descriptionFile: e.target.files[0]})} 
                />
              </div>
              
              <div className="form-actions">
                {editingRoleId ? (
                  <>
                    <button onClick={handleUpdateRole} className="btn-success"><FaSave /> Update Role</button>
                    <button onClick={() => { setEditingRoleId(null); setRoleForm({ title: "", location: "", type: "Full-Time", description: "" }); }} className="btn-secondary">Cancel</button>
                  </>
                ) : (
                  <button onClick={handleCreateRole}><FaPlus /> Create Role</button>
                )}
              </div>
            </div>

            <ul className="vertical-list">
              {roles.map(r => (
                <li key={r.id}>
                  <div><strong>{r.title}</strong> <small>({r.type}) - {r.location}</small></div>
                  <div className="actions">
                    <button className="reply-btn btn-warning mr-5" onClick={() => startEditRole(r)}><FaUserEdit /></button>
                    <button className="delete-btn" onClick={() => handleDelete('roles', r.id, fetchData)}><FaTrash /></button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 4. QUALIFICATIONS */}
        {section === "qualifications" && (
          <div className="manage-section">
            <h2>Manage Qualifications</h2>
            <div className="add-input">
              <input 
                 placeholder="Qualification Name (e.g. Bachelor's Degree)" 
                 value={qualForm.title} 
                 onChange={e => setQualForm({...qualForm, title: e.target.value})} 
              />
              <button onClick={() => handleCreate('qualifications', qualForm, setQualForm, {title:""}, fetchData)}>
                 <FaPlus /> Add
              </button>
            </div>
            <ul className="vertical-list">
               {qualifications.map(q => (
                 <li key={q.id}>
                   <div><strong>{q.title}</strong></div>
                   <button className="delete-btn" onClick={() => handleDelete('qualifications', q.id, fetchData)}>
                     <FaTrash />
                   </button>
                 </li>
               ))}
            </ul>
          </div>
        )}

        {/* 5. USERS MANAGEMENT */}
        {section === "users" && (
          <div className="manage-section">
            <h2>{editingUserId ? "Edit User" : "Manage Users"}</h2>
            
            <div className="add-input form-column">
              <div className="form-row">
                <input 
                  className="flex-1"
                  placeholder="Username" 
                  value={userForm.username} 
                  onChange={e => setUserForm({...userForm, username: e.target.value})} 
                />
                <input 
                  className="flex-1"
                  placeholder="Email" 
                  value={userForm.email} 
                  onChange={e => setUserForm({...userForm, email: e.target.value})} 
                />
                <select 
                  className="form-select"
                  value={userForm.role} 
                  onChange={e => setUserForm({...userForm, role: e.target.value})}
                >
                  <option value="Admin">Admin</option>
                  <option value="Editor">Editor</option>
                  <option value="Viewer">Viewer</option>
                </select>
              </div>

              {!editingUserId && (
                <input 
                  className="full-width-input"
                  placeholder="Password" 
                  type="password" 
                  value={userForm.password} 
                  onChange={e => setUserForm({...userForm, password: e.target.value})} 
                />
              )}

              <div className="form-actions">
                {editingUserId ? (
                  <>
                    <button onClick={handleUpdateUser} className="btn-success">
                      <FaSave /> Update Details
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => { 
                        setEditingUserId(null); 
                        setUserForm({ username: "", password: "", email: "", role: "Editor" }); 
                      }} 
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button onClick={() => handleCreate('users', userForm, setUserForm, {username:"", password:"", email:"", role:"Editor"}, fetchData)}>
                    <FaPlus /> Add New User
                  </button>
                )}
              </div>
            </div>

            <ul className="vertical-list">
              {users.map(u => (
                <li key={u.id}>
                  <div>
                    <strong>{u.username}</strong> 
                    <span className="user-role-text">
                      ({u.role}) - {u.email}
                    </span>
                  </div>
                  <div className="actions">
                    <button 
                      className="reply-btn btn-warning mr-5" 
                      onClick={() => startEditUser(u)} 
                      title="Edit User Info"
                    >
                      <FaUserEdit />
                    </button>
                    <button 
                      className="btn-dark mr-5"
                      onClick={() => handlePasswordReset(u.id)} 
                      title="Change Password"
                    >
                      <FaCogs /> Pwd
                    </button>
                    <button 
                      className="delete-btn" 
                      onClick={() => handleDelete('users', u.id, fetchData)}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 6. SERVICES MODULE */}
        {section === "services" && <ServiceManager token={token} />}

        {/* 7. BLOG MODULE */}
        {section === "blog" && <BlogManager token={token} />}

        {/* 8. AUDIT LOGS */}
        {section === "audit" && (
          <div className="audit-section">
            <h2>System Audit Logs</h2>
            <button className="refresh-btn mb-15" onClick={() => fetchAudit()}>Refresh Logs</button>
            <div className="audit-panel">
              {auditLogs.map(l => (
                <div key={l.id} className="audit-item">
                   <strong>{l.action}</strong> - {l.adminEmail} <small>{new Date(l.createdAt).toLocaleString()}</small>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 9. PROFILE */}
        {section === "profile" && (
          <div>
            <h2>Admin Profile</h2>
            <div className="manage-section">
                <input placeholder="Name" value={profileForm.name} onChange={(e)=> setProfileForm(p => ({...p,name:e.target.value}))} />
                <button onClick={() => alert("Profile updates implemented later.")}><FaSave /> Save Profile</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}